import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KYC } from "./useProfileApi";
import { IconCheck, IconClock, IconX, IconMinus } from "./icons";

const R = 24;
const C = 2 * Math.PI * R; // длина окружности кольца
const DONE = { [KYC.NONE]: 0, [KYC.PENDING]: 1, [KYC.REJECTED]: 1, [KYC.VERIFIED]: 2 };

/**
 * Карточка статуса верификации.
 * status: KYC.* или null (идёт загрузка)
 * to: если передан — для none/rejected показывается кнопка-ссылка на страницу верификации
 */
export default function VerifyCard({ id, status, to }) {
  const { t } = useTranslation();
  const loading = status === null || status === undefined;
  const known = !loading && status in DONE;
  const done = known ? DONE[status] : 0;

  const copy = {
    [KYC.NONE]: [
      t("profile.verify.noneTitle", "Аккаунт не верифицирован"),
      t("profile.verify.noneText", "Загрузите документ и селфи, чтобы подтвердить личность."),
    ],
    [KYC.PENDING]: [
      t("profile.verify.pendingTitle", "Документы на проверке"),
      t("profile.verify.pendingText", "Мы проверяем ваши данные. Результат появится здесь."),
    ],
    [KYC.VERIFIED]: [
      t("profile.verify.verifiedTitle", "Верификация пройдена"),
      t("profile.verify.verifiedText", "Ваша личность подтверждена."),
    ],
    [KYC.REJECTED]: [
      t("profile.verify.rejectedTitle", "Верификация отклонена"),
      t("profile.verify.rejectedText", "Проверьте данные и загрузите документы заново."),
    ],
    [KYC.UNKNOWN]: [
      t("profile.verify.unknownTitle", "Статус недоступен"),
      t("profile.verify.unknownText", "Не удалось получить статус верификации. Обновите страницу."),
    ],
  };
  const [title, text] = loading
    ? [t("profile.verify.loading", "Проверяем статус…"), ""]
    : copy[status] || copy[KYC.UNKNOWN];

  const docsBadge =
    status === KYC.NONE
      ? { tone: "pending", icon: <IconClock />, text: t("profile.verify.docsNone", "Документы не загружены") }
      : { tone: "ok", icon: <IconCheck />, text: t("profile.verify.docsSent", "Документы отправлены") };

  const checkBadge = {
    [KYC.NONE]: { tone: "idle", icon: <IconMinus />, text: t("profile.verify.checkNone", "Проверка не начата") },
    [KYC.PENDING]: { tone: "pending", icon: <IconClock />, text: t("profile.verify.checkPending", "На проверке") },
    [KYC.VERIFIED]: { tone: "ok", icon: <IconCheck />, text: t("profile.verify.checkOk", "Проверка пройдена") },
    [KYC.REJECTED]: { tone: "bad", icon: <IconX />, text: t("profile.verify.checkRejected", "Проверка отклонена") },
  }[status];

  const canStart = to && (status === KYC.NONE || status === KYC.REJECTED);

  return (
    <section className="fx-verify fx-section" id={id} data-s={loading ? "loading" : status}>
      <div className="fx-ring" role="img" aria-label={`${done} / 2`}>
        <svg viewBox="0 0 56 56" aria-hidden="true">
          <circle className="bg" cx="28" cy="28" r={R} />
          <circle
            className="fg"
            cx="28"
            cy="28"
            r={R}
            strokeDasharray={C}
            strokeDashoffset={C * (1 - done / 2)}
          />
        </svg>
        <span className="mono">{loading ? "…" : known ? `${done}/2` : "?"}</span>
      </div>

      <div className="fx-verify-text">
        <b>{title}</b>
        {text ? <p>{text}</p> : null}
        {known ? (
          <div className="fx-badges">
            <span className={`fx-badge ${docsBadge.tone}`}>{docsBadge.icon}{docsBadge.text}</span>
            {checkBadge ? (
              <span className={`fx-badge ${checkBadge.tone}`}>{checkBadge.icon}{checkBadge.text}</span>
            ) : null}
          </div>
        ) : null}
      </div>

      {canStart ? (
        <Link className="fx-btn fx-btn-line" to={to}>
          {status === KYC.REJECTED
            ? t("profile.verify.retry", "Отправить заново")
            : t("profile.verify.start", "Пройти верификацию")}
        </Link>
      ) : null}
    </section>
  );
}
